/**
 * Component: sa-shop-product
 * Handles:
 *   1. Color swatch click → variant switch (image, price line 1 when auto, buy link)
 *   2. Mobile carousel prev/next + progress
 */
if (!customElements.get('sa-shop-product')) {

  class SaShopProduct extends HTMLElement {
    constructor() {
      super();
      this.handleSwatchClick = this.handleSwatchClick.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleTaglineMore = this.handleTaglineMore.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.sa-shop-product__track');
      this.prevBtn = this.querySelector('.sa-shop-product__ctrl-btn--prev');
      this.nextBtn = this.querySelector('.sa-shop-product__ctrl-btn--next');
      this.progressBar = this.querySelector('.sa-shop-product__progress-bar');

      this.addEventListener('click', this.handleSwatchClick);
      this.addEventListener('click', this.handleTaglineMore);
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleNext);
      if (this.track) this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      this.updateProgress();
      this.initTaglines();
      this.alignInfoHeights();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleSwatchClick);
      this.removeEventListener('click', this.handleTaglineMore);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleNext);
      if (this.track) this.track.removeEventListener('scroll', this.handleScroll);
    }

    handleSwatchClick(e) {
      const swatch = e.target.closest('.sa-shop-product__swatches .option-item');
      if (!swatch) return;

      const card = swatch.closest('.sa-shop-product__card');
      if (!card) return;

      const value = swatch.dataset.value || '';
      const position = parseInt(swatch.parentElement.dataset.position || '', 10);
      if (!value || !position) return;

      const activeImg = card.querySelector('.product-img.active');
      if (!activeImg) return;

      const parts = (activeImg.dataset.title || '').split(' / ');
      parts[position - 1] = value;
      const newTitle = parts.join(' / ');

      const allImgs = card.querySelectorAll('.product-img');
      const newImg = Array.from(allImgs).find(el => el.dataset.title === newTitle);
      if (!newImg) return;

      const variantId = newImg.dataset.id || '';
      const available = newImg.dataset.available === 'true';
      const price = newImg.dataset.price || '';

      activeImg.classList.remove('active');
      newImg.classList.add('active');

      const prevActive = swatch.parentElement.querySelector('.option-item.active');
      if (prevActive) prevActive.classList.remove('active');
      swatch.classList.add('active');

      if (card.dataset.autoPrice === 'true') {
        const priceAmount = card.querySelector('.sa-shop-product__price-amount');
        if (priceAmount && price) priceAmount.textContent = price;
      }

      const buyBtn = card.querySelector('.sa-shop-product__btn-buy');
      if (buyBtn) {
        const cartBase = buyBtn.dataset.cartBase;
        if (available && cartBase && variantId) {
          buyBtn.href = cartBase + '/' + variantId + ':1';
          buyBtn.classList.remove('sa-shop-product__btn-buy--disabled');
        } else {
          buyBtn.href = '#';
          buyBtn.classList.add('sa-shop-product__btn-buy--disabled');
        }
      }
    }

    initTaglines() {
      const taglines = this.querySelectorAll('.sa-shop-product__tagline');
      taglines.forEach(el => {
        const style = getComputedStyle(el);
        let lh = parseFloat(style.lineHeight);
        if (isNaN(lh)) lh = parseFloat(style.fontSize) * 1.4;
        if (el.scrollHeight > lh * 3 + 1) {
          el.classList.add('sa-shop-product__tagline--clamped');
        }
      });
    }

    handleTaglineMore(e) {
      const btn = e.target.closest('.sa-shop-product__tagline-more');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const tagline = btn.closest('.sa-shop-product__tagline');
      if (tagline) {
        tagline.classList.remove('sa-shop-product__tagline--clamped');
        tagline.classList.add('sa-shop-product__tagline--expanded');
        this.alignInfoHeights();
      }
    }

    alignInfoHeights() {
      const infos = this.querySelectorAll('.sa-shop-product__info');
      if (!infos.length) return;
      infos.forEach(el => { el.style.minHeight = ''; });
      let max = 0;
      infos.forEach(el => { max = Math.max(max, el.offsetHeight); });
      if (max > 0) {
        infos.forEach(el => { el.style.minHeight = max + 'px'; });
      }
    }

    getCardWidth() {
      if (!this.track) return 200;
      const card = this.track.querySelector('.sa-shop-product__card');
      if (!card) return 200;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 10;
      return card.offsetWidth + gap;
    }

    handlePrev() {
      if (!this.track) return;
      this.track.scrollBy({ left: -this.getCardWidth(), behavior: 'smooth' });
    }

    handleNext() {
      if (!this.track) return;
      this.track.scrollBy({ left: this.getCardWidth(), behavior: 'smooth' });
    }

    handleScroll() {
      this.updateProgress();
    }

    updateProgress() {
      if (!this.track || !this.progressBar) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) {
        this.progressBar.style.width = '100%';
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        return;
      }
      const pct = (scrollLeft / maxScroll) * 100;
      this.progressBar.style.width = Math.min(100, Math.max(0, pct)) + '%';
      if (this.prevBtn) this.prevBtn.disabled = scrollLeft <= 1;
      if (this.nextBtn) this.nextBtn.disabled = scrollLeft >= maxScroll - 1;
    }
  }

  customElements.define('sa-shop-product', SaShopProduct);
}
